import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Container } from '@/components/generals/container'
import { Logo } from '@/components/generals/logo'
import { NewsletterForm } from '@/components/blog/newsletter-form'

import { siteConfig } from '@/constants/site'

const footerColumns = [
    {
        heading: 'Read',
        links: [
            { label: 'All stories', href: '/blog' },
            { label: 'Topics', href: '/topics' },
            { label: 'Authors', href: '/authors' },
            { label: 'Search', href: '/search' }
        ]
    },
    {
        heading: 'Journal',
        links: [
            { label: 'About', href: '/about' },
            { label: 'Newsletter', href: '#newsletter' },
            { label: 'RSS feed', href: '/rss.xml' }
        ]
    }
]

export const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='mt-24 sm:mt-32'>
            <section
                id='newsletter'
                aria-labelledby='newsletter-heading'
                className='scroll-mt-20 bg-primary text-primary-foreground'
            >
                <Container className='grid gap-10 py-16 sm:py-20 lg:grid-cols-12 lg:items-end lg:gap-16'>
                    <div className='lg:col-span-6'>
                        <p className='text-xs font-semibold uppercase tracking-[0.18em] text-brand-soft'>
                            The newsletter
                        </p>

                        <h2
                            id='newsletter-heading'
                            className='mt-4 font-heading text-4xl font-medium leading-[1.05] tracking-tight text-balance sm:text-5xl'
                        >
                            Good reading, <em className='italic text-brand-soft'>delivered</em> once a week.
                        </h2>

                        <p className='mt-5 max-w-md text-base leading-relaxed text-primary-foreground/70'>
                            Our newest essays and guides, sent straight to your inbox. Join readers who like their stories considered and their emails rare.
                        </p>
                    </div>

                    <div className='lg:col-span-5 lg:col-start-8'>
                        <NewsletterForm />
                    </div>
                </Container>
            </section>

            <div className='border-t border-border bg-background'>
                <Container className='py-14'>
                    <div className='grid gap-12 md:grid-cols-12'>
                        <div className='md:col-span-5'>
                            <Logo />

                            <p className='mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground'>
                                {siteConfig.description}
                            </p>
                        </div>

                        <nav aria-label='Footer' className='grid grid-cols-2 gap-8 md:col-span-6 md:col-start-7'>
                            {footerColumns.map(column => (
                                <div key={column.heading}>
                                    <h3 className='text-xs font-semibold uppercase tracking-[0.16em] text-foreground'>
                                        {column.heading}
                                    </h3>

                                    <ul className='mt-4 space-y-3'>
                                        {column.links.map(link => (
                                            <li key={link.href}>
                                                {link.href.startsWith('#') || link.href.endsWith('.xml')
                                                    ? (
                                                        <a href={link.href} className='text-sm text-muted-foreground transition-colors hover:text-foreground'>
                                                            {link.label}
                                                        </a>
                                                    )
                                                    : (
                                                        <Link href={link.href} className='text-sm text-muted-foreground transition-colors hover:text-foreground'>
                                                            {link.label}
                                                        </Link>
                                                    )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </nav>
                    </div>

                    <div className='mt-14 flex flex-col gap-2 border-t border-border pt-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between'>
                        <p suppressHydrationWarning>
                            &copy; {new Date().getUTCFullYear()} {siteConfig.name}. All rights reserved.
                        </p>

                        <p className='font-heading text-sm italic'>
                            {siteConfig.tagline}
                        </p>
                    </div>
                </Container>
            </div>
        </footer>
    )
}
